import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  useMediaQuery,
  Chip,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DateRange } from "@mui/icons-material";
import api from "../../utils/api";


export const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();
const isRenter = (v) => /\bRENTER\b/i.test(v);


const normalizeMembershipCode = (v) => {
  const c = canon(v);
  if (/^(SV|SC|SW|WF|WW)\b/.test(c)) {
    return c.match(/^(SV|SC|SW|WF|WW)/)[1]; // extract base code
  }
  return c;
};

export const groupNameFor = (raw) => {
  const c = normalizeMembershipCode(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

export const groupIdFor = (raw) => canon(groupNameFor(raw));



/* ------------------------- Helper Functions ------------------------- */
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function offsetDays(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function firstOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

const defaultStart = offsetDays(90);
const defaultEnd = todayISO();

/* ==================================================================== */
const WorkOrdersExportPage = () => {
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);


  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [membershipType, setMembershipType] = useState("");
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]);
  const [membershipTypeLoading, setMembershipTypeLoading] = useState(false);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const safeDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

  /* ------------------------ Flattened / Normalized Rows ------------------------ */
const normalizedData = useMemo(
  () =>
    filteredData.map((r) => {
      const rawCode = r.member?.ClubMembershipTypeCode || "";
      return {
        ...r,
        member_full_address:
          r.member?.AlternateAddress || r.member?.FullAddress || "",
        member_full_name: `${r.member?.FirstName || ""} ${
          r.member?.LastName || ""
        }`.trim(),
        membership_type: rawCode ? groupNameFor(rawCode) : "", // ✅ grouped readable name
        created_at: r.created_at ? r.created_at.slice(0, 10) : "",
        resolved_at: r.resolved_at ? r.resolved_at.slice(0, 10) : "",
      };
    }),
  [filteredData]
);
  /* ------------------------ Date Presets ------------------------ */
  const presets = [
    { key: "30d", label: "Last 30 days", get: () => ({ start: offsetDays(30), end: todayISO() }) },
    { key: "90d", label: "Last 90 days", get: () => ({ start: offsetDays(90), end: todayISO() }) },
    { key: "mtd", label: "MTD", get: () => ({ start: firstOfMonthISO(), end: todayISO() }) },
    { key: "ytd", label: "YTD", get: () => ({ start: `${new Date().getFullYear()}-01-01`, end: todayISO() }) },
  ];

  /* ------------------------ Fetch Data ------------------------ */
const buildMembershipTypeOptionsFromRows = (rows) => {
  const uniq = new Map();

  for (const r of rows || []) {
    const raw = r?.member?.ClubMembershipTypeCode;
    if (!raw) continue;

    const name = groupNameFor(raw);
    uniq.set(name, { value: name, label: name });
  }

  return Array.from(uniq.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};


  const fetchMembershipTypeOptions = async () => {
    setMembershipTypeLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);
      if (selectedType) params.append("type_id", selectedType);
      if (selectedSubtype) params.append("subtype_id", selectedSubtype);
      if (selectedLocation) params.append("location_id", selectedLocation);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      params.append("includeMember", "1");

      const { data } = await api.get(`/getWorkOrdersReport?${params.toString()}`);
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(data || []));
    } catch (e) {
      console.error("membership type options (report) fetch error:", e);
      setMembershipTypeOptions([]);
    } finally {
      setMembershipTypeLoading(false);
    }
  };

  const handleViewComments = async (workOrderId) => {
  if (!workOrderId) return;
  setCommentsModalOpen(true);
  setCommentsLoading(true);
  setSelectedWorkOrder(workOrderId);

  try {
    const res = await api.get(`/getComments/${workOrderId}`);
    setSelectedComments(res.data || []);
  } catch (err) {
    console.error("Error fetching comments:", err);
    setSelectedComments([]);
  } finally {
    setCommentsLoading(false);
  }
};

const fetchCommentsForOrders = async (orders) => {
  const enriched = await Promise.all(
    orders.map(async (order) => {
      try {
        const res = await api.get(`/getComments/${order.order_id}`);
        const comments = res.data || [];
        // Create a flat string summary for export
        const commentText = comments
          .map(
            (c) =>
              `${c.user_name} ${c.user_lastname}: "${c.comment_text}" (${new Date(
                c.sent_at
              ).toLocaleString()})`
          )
          .join(" | ");
        return { ...order, comments_summary: commentText };
      } catch (err) {
        return { ...order, comments_summary: "" };
      }
    })
  );
  return enriched;
};





  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [typesRes, subtypesRes, locationsRes, statusesRes] = await Promise.all([
          api.get("/workorder-types"),
          api.get("/workorder-subtypes"),
          api.get("/workorder-locations"),
          api.get("/statuses/workorders"),
        ]);
        setTypes(typesRes.data || []);
        setSubtypes(subtypesRes.data || []);
        setLocations(locationsRes.data || []);
        setStatuses(statusesRes.data || []);
      } catch (err) {
        console.error("Error fetching filter data:", err);
      }
    };
    fetchMembershipTypeOptions();
    fetchFilters();
  }, []);

  useEffect(() => {
    if (filteredData?.length) {
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(filteredData));
    }
  }, [filteredData]);

  /* ------------------------ Handlers ------------------------ */
  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedSubtype("");
    setSelectedLocation("");
  };

  const handleSubtypeChange = (e) => {
    setSelectedSubtype(e.target.value);
    setSelectedLocation("");
  };

  const handleReset = () => {
    setStatus("");
    setStartDate("");
    setEndDate("");
    setSelectedType("");
    setSelectedSubtype("");
    setSelectedLocation("");
    setPropertyId("");
    setMembershipType("");
    setFilteredData([]);
  };
const handleSearch = async () => {
  if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
    alert("Start Date must be before End Date.");
    return;
  }

  setLoading(true);
  try {
    const params = new URLSearchParams();

    if (status) params.append("status", status);
    if (startDate) params.append("start", startDate);
    if (endDate) params.append("end", endDate);
    if (selectedType) params.append("type_id", selectedType);
    if (selectedSubtype) params.append("subtype_id", selectedSubtype);
    if (selectedLocation) params.append("location_id", selectedLocation);
    if (propertyId.trim()) params.append("property_id", propertyId.trim());

    // ✅ Map friendly label → backend code
    if (membershipType.trim()) {
      const reverseMap = Object.fromEntries(
        Object.entries(GROUP_NAME_BY_CODE).map(([k, v]) => [v, k])
      );
      const code = reverseMap[membershipType.trim()] || membershipType.trim();
      params.append("club_membership_type_code", code);
    }

    params.append("includeMember", "1");

 const response = await api.get(`/getWorkOrdersReport?${params.toString()}`);
let data = Array.isArray(response.data) ? response.data : [];

// ✅ Fetch comments for each work order (for export)
data = await fetchCommentsForOrders(data);

setFilteredData(data);
  } catch (error) {
    console.error("Error fetching filtered work orders:", error);
  } finally {
    setLoading(false);
  }
};

  /* ------------------------ DataGrid Columns ------------------------ */
  const columns = useMemo(
    () => [
      { field: "order_id", headerName: "Order ID", width: 110 },
      { field: "property_id", headerName: "Member ID", width: 110 },
      { field: "membership_type", headerName: "Membership Type", width: 140 },
      { field: "member_full_name", headerName: "Member Name", width: 160 },
      {
        field: "member_full_address",
        headerName: "Member Full Address",
        width: 310,
        renderCell: (params) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
            {params?.value || ""}
          </div>
        ),
      },
      { field: "status", headerName: "Status", width: 140 },
      { field: "type_name", headerName: "Type", width: 140 },
      { field: "subtype_name", headerName: "Subtype", width: 180 },
      { field: "location_name", headerName: "Location", width: 160 },
      {
        field: "description",
        headerName: "Description",
        width: 200,
        renderCell: (params) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
            {params?.row?.description || ""}
          </div>
        ),
      },
      {
        field: "details",
        headerName: "Details",
        width: 320,
        renderCell: (params) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
            {params?.row?.details || ""}
          </div>
        ),
      },
      {
        field: "opened_by",
        headerName: "Opened By",
        width: 150,
        renderCell: (params) => {
          const row = params?.row || {};
          return (
            row.opened_by_name ||
            row.opened_by_username ||
            (row.opened_by_user_id ? `No User found ${row.opened_by_user_id}` : "")
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        width: 140,
        renderCell: (params) => safeDate(params?.value),
      },
      {
        field: "resolved_at",
        headerName: "Resolved At",
        width: 140,
        renderCell: (params) => safeDate(params?.value),
      },
      {
        field: "resolved_by",
        headerName: "Resolved By",
        width: 120,
        renderCell: (params) => {
          const row = params?.row || {};
          return row.resolved_by_name || row.resolved_by || "";
        },
      },
      {
  field: "comments",
  headerName: "Comments",
  width: 150,
  renderCell: (params) =>
    params?.row?.order_id ? (
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleViewComments(params.row.order_id)}
      >
        View Comments
      </Button>
    ) : (
      "-"
    ),
},
{
  field: "comments_summary",
  headerName: "Comments",
  width: 330,
  renderCell: (params) => (
    <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
      {params.value || "—"}
    </Typography>
  ),
},
    ],
    []
  );

  const GRID_MIN_WIDTH =
    110 + 120 + 180 + 310 + 140 + 160 + 180 + 160 + 220 + 420 + 180 + 140 + 140 + 180 + 80;

  const initialColumnVisibility = useMemo(
    () =>
      isSmall
        ? { member_full_address: false, description: false }
        : { member_full_address: true, description: true },
    [isSmall]
  );

  /* ------------------------ Render ------------------------ */
  return (
    <Box sx={{ width: filteredData.length > 0 ? "60%" : "100%", transition: "width 0.3s ease" }}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          📋 Work Orders Report
        </Typography>

      {/* Filters */}
<Grid container spacing={2} alignItems="center">
  {/* Member/Property ID */}
  <Grid item xs={12} md={3}>
    <TextField
      fullWidth
      label="Member/Property ID"
      type="text"
      InputLabelProps={{ shrink: true }}
      value={propertyId}
      onChange={(e) => setPropertyId(e.target.value)}
    />
  </Grid>

  {/* Status */}
  <Grid item xs={12} md={3}>
    <FormControl fullWidth>
      <InputLabel>Status</InputLabel>
      <Select
        value={status}
        label="Status"
        onChange={(e) => setStatus(e.target.value)}
      >
        <MenuItem value="">All</MenuItem>
        {statuses.map((s) => (
          <MenuItem key={s.id} value={s.statusname}>
            {s.statusname}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>

  {/* Date Range */}
  <Grid item xs={6} md={3}>
    <TextField
      fullWidth
      label="Start Date"
      type="date"
      InputLabelProps={{ shrink: true }}
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
    />
  </Grid>
  <Grid item xs={6} md={3}>
    <TextField
      fullWidth
      label="End Date"
      type="date"
      InputLabelProps={{ shrink: true }}
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
    />
  </Grid>

  {/* Type */}
  <Grid item xs={12} md={3}>
    <FormControl fullWidth>
      <InputLabel>Type</InputLabel>
      <Select
        value={selectedType}
        label="Type"
        onChange={handleTypeChange}
      >
        <MenuItem value="">All</MenuItem>
        {types.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.type}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>

  {/* Subtype */}
  <Grid item xs={12} md={3}>
    <FormControl fullWidth disabled={!selectedType}>
      <InputLabel>Subtype</InputLabel>
      <Select
        value={selectedSubtype}
        label="Subtype"
        onChange={handleSubtypeChange}
      >
        <MenuItem value="">All</MenuItem>
        {subtypes
          .filter((s) => s.type_id === parseInt(selectedType || "-1", 10))
          .map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.subtype_name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  </Grid>

  {/* Location */}
  <Grid item xs={12} md={3}>
    <FormControl fullWidth disabled={!selectedType}>
      <InputLabel>Location</InputLabel>
      <Select
        value={selectedLocation}
        label="Location"
        onChange={(e) => setSelectedLocation(e.target.value)}
      >
        <MenuItem value="">All</MenuItem>
        {locations
          .filter((l) => l.type_id === parseInt(selectedType || "-1", 10))
          .map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.location_name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  </Grid>

  {/* Membership Type */}
  <Grid item xs={12} md={3}>
    <FormControl fullWidth>
      <InputLabel>Membership Type</InputLabel>
      <Select
        value={membershipType}
        label="Membership Type"
        onChange={(e) => setMembershipType(e.target.value)}
        disabled={membershipTypeLoading}
      >
        <MenuItem value="">All</MenuItem>
        {membershipTypeOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>

  {/* Search Button */}
  <Grid item xs={12} md={3}>
    <Button fullWidth variant="contained" onClick={handleSearch}>
      Search
    </Button>
  </Grid>

  {/* Date Presets */}
  <Grid item xs={12} md={4}>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {presets.map((p) => {
        const { start, end } = p.get();
        const active = startDate === start && endDate === end;
        return (
          <Chip
            key={p.key}
            icon={<DateRange />}
            size="small"
            label={p.label}
            color={active ? "primary" : "default"}
            variant={active ? "filled" : "outlined"}
            onClick={() => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        );
      })}
    </Stack>
  </Grid>

  {/* Reset Button */}
  <Grid item xs={12} md={3}>
    <Button
      fullWidth
      variant="outlined"
      color="secondary"
      onClick={handleReset}
    >
      Reset Filters
    </Button>
  </Grid>
</Grid>

        {/* Results */}
        <Box mt={4}>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : filteredData.length > 0 ? (
            <>
              <Paper elevation={0}>
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Box sx={{ display: "inline-block", minWidth: `${GRID_MIN_WIDTH}px` }}>
                    <DataGrid
                      rows={normalizedData}
                      columns={columns}
                      getRowId={(r) =>
                        r.order_id ??
                        `${r.property_id}-${r.created_at}-${r.type_name}`
                      }
                      autoHeight
                      disableRowSelectionOnClick
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 300 },
                        },
                      }}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 25, page: 0 },
                        },
                        columns: {
                          columnVisibilityModel: initialColumnVisibility,
                        },
                      }}
                      pageSizeOptions={[10, 25, 50, 100]}
                      getRowHeight={() => "auto"}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ mt: 2, textAlign: "left", fontWeight: "bold" }}
                >
                  Total Records: {filteredData.length}
                </Typography>
              </Paper>
            </>
          ) : (
            <Typography>No results to display</Typography>
          )}
          <Dialog
  open={commentsModalOpen}
  onClose={() => setCommentsModalOpen(false)}
  fullWidth
  maxWidth="sm"
>
  <DialogTitle>
    Work Order {selectedWorkOrder ? `#${selectedWorkOrder}` : ""} Comments
  </DialogTitle>

  <DialogContent dividers>
    {commentsLoading ? (
      <Typography>Loading comments...</Typography>
    ) : selectedComments.length > 0 ? (
      selectedComments.map((c) => (
        <Box
          key={c.comment_id}
          sx={{
            mb: 1.5,
            p: 1,
            border: "1px solid #ddd",
            borderRadius: 1,
            backgroundColor: c.is_internal ? "#f9f9f9" : "white",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {c.user_name} {c.user_lastname}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {c.comment_text}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(c.sent_at).toLocaleString()}
          </Typography>
        </Box>
      ))
    ) : (
      <Typography variant="body2" color="text.secondary">
        No comments available for this work order.
      </Typography>
    )}
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setCommentsModalOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>
        </Box>
        
      </Box>
    </Box>
  );
};

export default WorkOrdersExportPage;
